import {
  INIT_WORKFLOW_PENDING,
  INIT_WORKFLOW_FULFILLED,
  INIT_WORKFLOW_REJECTED,
  FETCH_WORKFLOWS_PENDING,
  FETCH_WORKFLOWS_FULFILLED,
  FETCH_WORKFLOWS_REJECTED,
  FETCH_WORKFLOW_PENDING,
  FETCH_WORKFLOW_FULFILLED,
  FETCH_WORKFLOW_REJECTED,
  SILENT_FETCH_WORKFLOW_FULFILLED,
  PERFORM_WORKFLOW_STEP_ACTIONS_PENDING,
  PERFORM_WORKFLOW_STEP_ACTIONS_FULFILLED,
  PERFORM_WORKFLOW_STEP_ACTIONS_REJECTED,
  WORKFLOW_STEP_ERROR_RECEIVED,
  INIT_SITE_CREATION_WORKFLOW_PENDING,
  INIT_SITE_CREATION_WORKFLOW_FULFILLED,
  INIT_SITE_CREATION_WORKFLOW_REJECTED,
  WORKFLOW_TYPE_SITE_CREATION,
  WORKFLOW_SCHEMA_ERROR,
  WORKFLOW_STEP_ROLLBACK_PENDING,
  WORKFLOW_STEP_ROLLBACK_SUCCESSFUL,
  WORKFLOW_STEP_ROLLBACK_FAILED,
} from "./constants";
import config from "../../../config";
import { retryNTimes } from "../../../utils";
// @ts-ignore
import { log, error } from "../logger/api";
import {
  EVENT_PERFORM_ACTION_PENDING,
  EVENT_PERFORM_ACTION_SUCCEEDED,
  EVENT_PERFORM_ACTION_FAILED,
} from "../logger/constants";
// @ts-ignore
import stepShouldSwitchAutomatically from "./stepShouldSwitchAutomatically";
import flatten from "lodash/flatten";

import {
  generate,
  getCurrentCustomerId,
  getCurrentSiteId,
  ROUTE_ONBOARDING_CONTINUE,
} from "../route/routes";

import { pushRoute } from "../route/actions";
import { ensureSite } from "../sites/actions";

import { Workflow, WorkflowStep } from "./reducer";
import { AppDispatch, AppGetState } from "../../../store";
import { Err } from "../../../typings";
import { IWorkflowAction } from "../currentWorkflow/reducer";
import { AxiosRequestConfig, AxiosResponse } from "axios";
import { forceUpdateSiteLevelToken } from "../../accessTokenManagerActions";
import { globalContextRequest } from "../../globalContextRequest";
import { siteContextRequest } from "../../siteContextRequest";
import { getRouterState } from "../rootReducer";

const { backofficeWebService } = config.endpoints;

export function fetchCustomerLevelWorkflow(workflowId: string) {
  return function (dispatch: AppDispatch): Promise<Workflow> {
    dispatch({ type: FETCH_WORKFLOW_PENDING });
    return dispatch(
      globalContextRequest({
        method: "GET",
        url: `${backofficeWebService}/api/workflow/${workflowId}`,
      }),
    ).then(
      ({ data }) => {
        if (!isWorkflow(data)) {
          const err = {
            reason: "server",
            message: "Server response is not a workflow",
          };
          // HACK: TODO: handle this as a particular workflow step error (reducer)
          dispatch({ type: WORKFLOW_SCHEMA_ERROR, payload: err });
          throw err;
        }
        dispatch({ type: FETCH_WORKFLOW_FULFILLED, payload: data });
        return data;
      },
      (err: Error) => {
        dispatch({ type: FETCH_WORKFLOW_REJECTED, payload: err });
        throw err;
      },
    );
  };
}

export const fetchSiteLevelWorkflow =
  (customerId: string, siteId: string, workflowId: string) =>
  (dispatch: AppDispatch): Promise<Workflow> => {
    dispatch({ type: FETCH_WORKFLOW_PENDING });
    return dispatch(
      siteContextRequest(customerId, siteId, {
        method: "GET",
        url: `${backofficeWebService}/api/workflow/${workflowId}`,
      }),
    ).then(
      ({ data }) => {
        if (!isWorkflow(data)) {
          const err = {
            reason: "server",
            message: "Server response is not a workflow",
          };
          // HACK: TODO: handle this as a particular workflow step error (reducer)
          dispatch({ type: WORKFLOW_SCHEMA_ERROR, payload: err });
          throw err;
        }
        dispatch({ type: FETCH_WORKFLOW_FULFILLED, payload: data });
        return data;
      },
      (err: Error) => {
        dispatch({ type: FETCH_WORKFLOW_REJECTED, payload: err });
        throw err;
      },
    );
  };

// silentFetchWorkflow does'nt emit pending and error actions and respects workflow updateTime
export function silentFetchWorkflow(
  customerId: string,
  siteId: string,
  workflowId: string,
) {
  return function (dispatch: AppDispatch): Promise<Workflow> {
    return dispatch(
      siteContextRequest(customerId, siteId, {
        method: "GET",
        url: `${backofficeWebService}/api/workflow/${workflowId}`,
      }),
    ).then(
      ({ data }) => {
        if (!isWorkflow(data)) {
          throw {
            reason: "server",
            message: "Server response is not a workflow",
          };
        }
        dispatch({ type: SILENT_FETCH_WORKFLOW_FULFILLED, payload: data });
        return data;
      },
      () => undefined,
    );
  };
}

export function loadWorkflows(
  customerId: string,
  siteId: string,
  workflowType: string,
) {
  // TODO: specify Promise type
  return function (dispatch: AppDispatch): Promise<void> {
    dispatch({ type: FETCH_WORKFLOWS_PENDING });
    return dispatch(
      makeWorkflowListRequest(customerId, siteId, workflowType),
    ).then(
      (resp: Workflow[]) => {
        dispatch({ type: FETCH_WORKFLOWS_FULFILLED, payload: resp });
      },
      (err: Error) => {
        dispatch({ type: FETCH_WORKFLOWS_REJECTED, payload: err });
        throw err;
      },
    );
  };
}

function makeWorkflowListRequest(
  customerId: string,
  siteId: string,
  workflowType: string,
) {
  return function (dispatch: AppDispatch): Promise<any> {
    return dispatch(
      siteContextRequest(customerId, siteId, {
        method: "GET",
        url: `${backofficeWebService}/api/customer/${customerId}/site/${siteId}/workflow/${workflowType.toLowerCase()}/list`,
      }),
    ).then((values) => values.data);
  };
}

export function performWorkflowActions(
  workflowId: string,
  workflowType: string,
  step: WorkflowStep,
  actions: IWorkflowAction[],
  customerId: string,
  siteId: string | null,
) {
  return function (
    dispatch: AppDispatch,
    getState: AppGetState,
  ): Promise<Workflow | void> {
    const stepId = step.id;

    dispatch({
      type: PERFORM_WORKFLOW_STEP_ACTIONS_PENDING,
      payload: { workflowId, stepId },
    });

    const url = resolvePerformActionUrl(customerId, siteId, workflowType);

    const performActionPayload = {
      workflowId,
      stepId,
      actions: actions.map(({ id, value }) => ({ actionId: id, value })),
    };

    log(
      EVENT_PERFORM_ACTION_PENDING,
      "1",
      JSON.stringify({
        url,
        performActionPayload,
      }),
      true,
    );

    function createRequest(): Promise<any> {
      const axiosRequestConfig: AxiosRequestConfig = {
        method: "POST",
        url,
        data: performActionPayload,
      };

      const routerState = getRouterState(getState());
      const customerId = getCurrentCustomerId(routerState);
      const siteId = getCurrentSiteId(routerState);
      if (customerId && siteId) {
        return dispatch(
          siteContextRequest(customerId, siteId, axiosRequestConfig),
        );
      }
      return dispatch(globalContextRequest(axiosRequestConfig));
    }

    if (stepShouldSwitchAutomatically(step)) {
      return retryNTimes(
        createRequest,
        config.performActionRetryTimeout,
        config.performActionRetryLimits,
      ).then(handleSuccess, () => {
        handleError({
          type: "network",
          message: "could not send perform-action. timeouts exceeded",
        });
      });
    } else {
      return createRequest().then(handleSuccess, handleError);
    }

    function handleSuccess(response: AxiosResponse) {
      const { data } = response;
      if (!isWorkflow(data)) {
        const err = {
          reason: "server",
          message: "Server response is not a workflow",
        };
        dispatch({ type: WORKFLOW_SCHEMA_ERROR, payload: err });
        error(
          EVENT_PERFORM_ACTION_FAILED,
          "1",
          `Server response is not a workflow ${url}`,
          true,
        );
        throw err;
      }
      if (data.workflowType === WORKFLOW_TYPE_SITE_CREATION) {
        // update token when step is performed on SITE_CREATION workflow
        return handleOnboardingWorkflow(
          dispatch,
          getState,
          data,
          customerId,
        ).then(() => {
          dispatch({
            type: PERFORM_WORKFLOW_STEP_ACTIONS_FULFILLED,
            payload: data,
          });
          return data;
        });
      }
      dispatch({
        type: PERFORM_WORKFLOW_STEP_ACTIONS_FULFILLED,
        payload: data,
      });
      log(EVENT_PERFORM_ACTION_SUCCEEDED, "1", url, true);
      return data;
    }

    function handleError(err: any) {
      dispatch({
        type: PERFORM_WORKFLOW_STEP_ACTIONS_REJECTED,
        payload: { workflowId, stepId },
      });

      const mainError = {
        type: err.type,
        message: err.message,
        payload: { workflowId, stepId, url },
      };

      const actionErrors = err.payload && err.payload.errors;
      if (actionErrors) {
        actionErrors.map(({ actionId, message }: any) => {
          const actionError = {
            type: "client",
            message: message,
            payload: { workflowId, stepId, actionId, url },
          };
          dispatch({
            type: WORKFLOW_STEP_ERROR_RECEIVED,
            payload: actionError,
          });
          error(EVENT_PERFORM_ACTION_FAILED, "1", `${url} ${message}`, true);
        });
      } else {
        dispatch({ type: WORKFLOW_STEP_ERROR_RECEIVED, payload: mainError });
        error(
          EVENT_PERFORM_ACTION_FAILED,
          "1",
          `${url} ${mainError.message}`,
          true,
        );
      }

      throw mainError;
    }
  };
}

function resolvePerformActionUrl(
  customerId: string,
  siteId: string | null,
  workflowType: string,
): string {
  if (workflowType === WORKFLOW_TYPE_SITE_CREATION) {
    return `${backofficeWebService}/api/customer/${customerId}/workflow/${workflowType.toLowerCase()}/action`;
  }

  return siteId
    ? `${backofficeWebService}/api/customer/${customerId}/site/${siteId}/workflow/${workflowType.toLowerCase()}/action`
    : `${backofficeWebService}/api/customer/${customerId}/workflow/${workflowType.toLowerCase()}/action`;
}

async function handleOnboardingWorkflow(
  dispatch: AppDispatch,
  getState: AppGetState,
  workflow: Workflow,
  customerId: string,
) {
  await dispatch(forceUpdateSiteLevelToken(customerId, workflow.siteId));
  return dispatch(ensureSite(workflow.customerId, workflow.siteId)).then(() => {
    dispatch(
      pushRoute(
        generate(ROUTE_ONBOARDING_CONTINUE, {
          customerId: workflow.customerId,
          siteId: workflow.siteId,
          workflowId: workflow.id,
        }),
      ),
    );
  });
}

export function initWorkflow(
  customerId: string,
  siteId: string,
  workflowType: string,
) {
  return function (dispatch: AppDispatch): Promise<Workflow | Err> {
    dispatch({ type: INIT_WORKFLOW_PENDING });
    return dispatch(
      siteContextRequest(customerId, siteId, {
        method: "POST",
        url: `${backofficeWebService}/api/customer/${customerId}/site/${siteId}/workflow/${workflowType.toLowerCase()}/init`,
      }),
    ).then(
      ({ data }) => {
        if (!isWorkflow(data)) {
          const err = {
            reason: "server",
            message: "Server response is not a workflow",
          };
          dispatch({ type: WORKFLOW_SCHEMA_ERROR, payload: err });
          throw err;
        }
        dispatch({ type: INIT_WORKFLOW_FULFILLED, payload: data });
        return data;
      },
      (err: Err) => {
        dispatch({ type: INIT_WORKFLOW_REJECTED, payload: err });
        throw err;
      },
    );
  };
}

export function initSiteCreationWorkflow(customerId: string) {
  return (dispatch: AppDispatch) => {
    dispatch({ type: INIT_SITE_CREATION_WORKFLOW_PENDING });
    return dispatch(
      globalContextRequest({
        method: "POST",
        url: `${backofficeWebService}/api/customer/${customerId}/new_site/init`,
      }),
    ).then(
      ({ data }) => {
        if (!isWorkflow(data)) {
          const err = {
            reason: "server",
            message: "Server response is not a workflow",
          };
          dispatch({ type: WORKFLOW_SCHEMA_ERROR, payload: err });
          throw err;
        }
        dispatch({
          type: INIT_SITE_CREATION_WORKFLOW_FULFILLED,
          payload: data,
        });
        return data;
      },
      (err: Err) => {
        dispatch({ type: INIT_SITE_CREATION_WORKFLOW_REJECTED, payload: err });
        throw err;
      },
    );
  };
}

export function rollbackWorkflowStep(
  customerId: string,
  siteId: string | null,
  workflowId: string,
  stepId: number,
) {
  return async (dispatch: AppDispatch) => {
    if (!siteId) {
      return;
    }
    try {
      dispatch({ type: WORKFLOW_STEP_ROLLBACK_PENDING });
      const axiosRequestConfig: AxiosRequestConfig = {
        method: "PATCH",
        url: `${backofficeWebService}/api/customer/${customerId}/site/${siteId}/workflow/${workflowId}`,
        data: {
          op: "resetToStep",
          stepId: stepId,
        },
      };
      await dispatch(
        siteContextRequest(customerId, siteId, axiosRequestConfig),
      );
      dispatch({ type: WORKFLOW_STEP_ROLLBACK_SUCCESSFUL });
    } catch (err) {
      dispatch({ type: WORKFLOW_STEP_ROLLBACK_FAILED, payload: err });
      throw err;
    }
    await dispatch(fetchSiteLevelWorkflow(customerId, siteId, workflowId));
  };
}

function isWorkflow(workflow: Workflow) {
  return workflow && workflow.id;
}
